import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Music, SkipForward, ShieldCheck, LogOut, Volume2, ListMusic } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { decodeYoutubeTitle } from '../lib/sanitizeText';

/**
 * DJ / Music Phone panel.
 *
 * - Auto-plays the next queued song using the YouTube IFrame Player API.
 * - Polls `/api/music-queue` every 4 s to refresh the queue + flagged list.
 * - When the player fires onStateChange = ENDED → marks complete and pulls
 *   the next track without operator action (per owner spec: "auto-play
 *   next song in queue, no approval needed").
 * - Skip / Approve buttons let the DJ override any item.
 */

const YT_SCRIPT_ID = 'majnu-yt-iframe-api';
const POLL_MS = 4000;

function loadYouTubeIframeApi() {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) { resolve(window.YT); return; }
    if (document.getElementById(YT_SCRIPT_ID)) {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(window.YT); };
      return;
    }
    const tag = document.createElement('script');
    tag.id = YT_SCRIPT_ID;
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });
}

export default function DJPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [queue, setQueue] = useState([]);     // queued + playing + flagged
  const [nowPlaying, setNowPlaying] = useState(null);
  const [apiReady, setApiReady] = useState(false);
  const [error, setError] = useState(null);

  const playerRef = useRef(null);
  const playerElRef = useRef(null);
  const startingRef = useRef(false);          // de-dupe simultaneous start attempts
  const lastVideoIdRef = useRef(null);

  // ---------- API helpers ----------
  const fetchQueue = useCallback(async () => {
    try {
      const { data } = await api.get('/music-queue');
      setQueue(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load queue');
    }
  }, []);

  const markPlaying = useCallback(async (id) => {
    try { await api.post(`/music-queue/${id}/playing`); } catch (e) { /* swallow — backend will reconcile */ }
  }, []);

  const markComplete = useCallback(async (id) => {
    try { await api.post(`/music-queue/${id}/complete`); } catch (_e) { /* idempotent */ }
  }, []);

  const skipTrack = useCallback(async (id) => {
    try {
      await api.post(`/music-queue/${id}/skip`);
      toast.success('Skipped');
      await fetchQueue();
    } catch (e) { toast.error(e.response?.data?.detail || 'Skip failed'); }
  }, [fetchQueue]);

  const approveTrack = useCallback(async (id) => {
    try {
      await api.post(`/music-queue/${id}/approve`);
      toast.success('Approved — added to queue');
      await fetchQueue();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  }, [fetchQueue]);

  // ---------- Player ----------
  // Picks the next thing to play: a 'queued' track in submission order. If
  // nothing is queued but something is still marked 'playing', return that
  // (handles page refresh mid-track).
  const pickNext = useCallback(() => {
    const playing = queue.find(q => q.status === 'playing');
    if (playing) return playing;
    return queue.find(q => q.status === 'queued') || null;
  }, [queue]);

  const startTrack = useCallback(async (track) => {
    if (!playerRef.current || !track || startingRef.current) return;
    if (lastVideoIdRef.current === track.id) return;       // already on this track
    startingRef.current = true;
    lastVideoIdRef.current = track.id;
    try {
      // Mark on the server FIRST so a refresh resumes mid-track instead of
      // double-playing it on another DJ tab.
      if (track.status !== 'playing') await markPlaying(track.id);
      playerRef.current.loadVideoById({ videoId: track.video_id });
      setNowPlaying(track);
    } finally {
      startingRef.current = false;
    }
  }, [markPlaying]);

  // Load YouTube IFrame API + instantiate player once.
  useEffect(() => {
    let cancelled = false;
    loadYouTubeIframeApi().then((YT) => {
      if (cancelled || !playerElRef.current || playerRef.current) return;
      playerRef.current = new YT.Player(playerElRef.current, {
        height: '100%', width: '100%',
        playerVars: { autoplay: 1, controls: 1, rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onReady: () => setApiReady(true),
          onStateChange: async (ev) => {
            // ENDED = 0
            if (ev.data === 0 && lastVideoIdRef.current) {
              const finishedId = lastVideoIdRef.current;
              lastVideoIdRef.current = null;
              await markComplete(finishedId);
              await fetchQueue();   // refresh — pickNext effect below will grab next
            }
            // ERROR / unstarted handled by ERROR event; ignore here.
          },
          onError: async () => {
            // Bad / region-locked video — skip it so the queue keeps moving.
            if (lastVideoIdRef.current) {
              const badId = lastVideoIdRef.current;
              lastVideoIdRef.current = null;
              try { await api.post(`/music-queue/${badId}/skip`); } catch (_e) { /* */ }
              await fetchQueue();
            }
          }
        }
      });
    });
    return () => {
      cancelled = true;
      if (playerRef.current && playerRef.current.destroy) {
        try { playerRef.current.destroy(); } catch (_e) { /* */ }
        playerRef.current = null;
      }
    };
  }, [fetchQueue, markComplete]);

  // Poll queue every POLL_MS.
  useEffect(() => {
    fetchQueue();
    const t = setInterval(fetchQueue, POLL_MS);
    return () => clearInterval(t);
  }, [fetchQueue]);

  // When the API is ready and we have a candidate next track that isn't
  // already what we're playing, kick it off. Auto-play only — no approval.
  useEffect(() => {
    if (!apiReady) return;
    const next = pickNext();
    if (!next) {
      // Nothing to play — clear the now-playing card so the UI is honest.
      if (nowPlaying) setNowPlaying(null);
      return;
    }
    if (lastVideoIdRef.current !== next.id) {
      startTrack(next);
    } else {
      // Sync nowPlaying card to whatever the server has, in case status changed.
      setNowPlaying(next);
    }
  }, [apiReady, queue, pickNext, startTrack, nowPlaying]);

  // ---------- View ----------
  const queued = queue.filter(q => q.status === 'queued');
  const flagged = queue.filter(q => q.status === 'flagged');

  return (
    <div className="min-h-screen bg-bg-primary text-white flex flex-col" data-testid="dj-panel">
      {/* Header */}
      <header className="px-4 lg:px-6 py-3 border-b border-white/5 flex items-center justify-between sticky top-0 z-30 bg-bg-primary/95 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
            <Music className="w-4 h-4 text-gold" />
          </div>
          <div>
            <h1 className="font-serif text-lg leading-none">DJ Booth</h1>
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans mt-1">
              Auto-playing customer requests
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-xs text-zinc-500 font-sans">{user?.name}</span>
          <button onClick={() => { logout(); navigate('/login'); }} className="text-zinc-500 hover:text-red-400 p-2" data-testid="dj-logout">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 p-4 lg:p-6">
        {/* Player + now playing */}
        <div className="space-y-3">
          <div className="relative aspect-video bg-black border border-white/10 rounded-sm overflow-hidden shadow-2xl">
            <div ref={playerElRef} className="absolute inset-0" data-testid="dj-player" />
            {!nowPlaying && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <Music className="w-10 h-10 text-zinc-700 mb-3" />
                <p className="font-serif text-zinc-400 text-lg">Queue is empty</p>
                <p className="text-xs text-zinc-600 font-sans mt-1">Waiting for the next customer request…</p>
              </div>
            )}
          </div>

          {nowPlaying && (
            <div className="flex items-center gap-3 p-3 border border-gold/20 bg-gold/5 rounded-sm" data-testid="dj-now-playing">
              <Volume2 className="w-5 h-5 text-gold flex-shrink-0 animate-pulse" />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-sans font-bold">Now Playing</p>
                <p className="font-serif text-white text-base truncate">{decodeYoutubeTitle(nowPlaying.title)}</p>
                <p className="text-xs text-zinc-500 font-sans truncate">
                  {nowPlaying.channel_title}
                  {nowPlaying.requester_name ? <> · req. by <span className="text-zinc-300">{nowPlaying.requester_name}</span></> : null}
                  {nowPlaying.table_id ? <> · table {nowPlaying.table_id}</> : null}
                </p>
              </div>
              <Button onClick={() => skipTrack(nowPlaying.id)} className="bg-transparent border border-white/10 text-zinc-300 hover:border-red-500/50 hover:text-red-400 rounded-sm h-9 px-3" data-testid="dj-skip-now">
                <SkipForward className="w-4 h-4 mr-1.5" /> Skip
              </Button>
            </div>
          )}

          {error && (
            <p className="text-xs text-red-300 font-sans bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2">{error}</p>
          )}
        </div>

        {/* Queue + flagged */}
        <aside className="space-y-4">
          <section>
            <div className="flex items-center justify-between mb-2 px-1">
              <h2 className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-sans font-bold flex items-center gap-1.5">
                <ListMusic className="w-3.5 h-3.5" /> Up Next
              </h2>
              <span className="text-[10px] text-zinc-600 font-mono" data-testid="dj-queue-count">{queued.length}</span>
            </div>
            <div className="space-y-1.5" data-testid="dj-queue-list">
              {queued.length === 0 && (
                <p className="text-xs text-zinc-600 font-sans italic px-2 py-3">Nothing queued.</p>
              )}
              {queued.map((it, idx) => (
                <div key={it.id} className="flex items-center gap-2.5 p-2 rounded-sm border border-white/5 bg-white/[0.02]" data-testid={`dj-queue-item-${it.id}`}>
                  <span className="text-[10px] font-mono text-zinc-600 w-4 text-center">{idx + 1}</span>
                  {it.thumbnail_url ? (
                    <img src={it.thumbnail_url} alt="" className="w-10 h-10 rounded-sm object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-sm bg-white/5 flex items-center justify-center flex-shrink-0"><Music className="w-4 h-4 text-zinc-600" /></div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-serif text-white text-xs leading-tight line-clamp-2">{decodeYoutubeTitle(it.title)}</p>
                    <p className="text-[10px] text-zinc-500 font-sans truncate mt-0.5">
                      {it.requester_name || 'Guest'}{it.table_id ? ` · T${it.table_id}` : ''}
                    </p>
                  </div>
                  <button onClick={() => skipTrack(it.id)} className="p-1.5 text-zinc-500 hover:text-red-400" title="Skip" data-testid={`dj-skip-${it.id}`}>
                    <SkipForward className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {flagged.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-2 px-1">
                <h2 className="text-[10px] uppercase tracking-[0.22em] text-amber-400 font-sans font-bold">⚑ Flagged for Review</h2>
                <span className="text-[10px] text-zinc-600 font-mono">{flagged.length}</span>
              </div>
              <div className="space-y-1.5" data-testid="dj-flagged-list">
                {flagged.map((it) => (
                  <div key={it.id} className="flex items-center gap-2.5 p-2 rounded-sm border border-amber-500/30 bg-amber-500/[0.04]" data-testid={`dj-flagged-item-${it.id}`}>
                    {it.thumbnail_url ? (
                      <img src={it.thumbnail_url} alt="" className="w-10 h-10 rounded-sm object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-sm bg-white/5 flex items-center justify-center flex-shrink-0"><Music className="w-4 h-4 text-zinc-600" /></div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-white text-xs leading-tight line-clamp-2">{decodeYoutubeTitle(it.title)}</p>
                      <p className="text-[10px] text-zinc-500 font-sans truncate mt-0.5">{it.requester_name || 'Guest'}</p>
                    </div>
                    <button onClick={() => approveTrack(it.id)} className="p-1.5 text-emerald-400 hover:text-emerald-300" title="Approve" data-testid={`dj-approve-${it.id}`}>
                      <ShieldCheck className="w-4 h-4" />
                    </button>
                    <button onClick={() => skipTrack(it.id)} className="p-1.5 text-zinc-500 hover:text-red-400" title="Reject" data-testid={`dj-reject-${it.id}`}>
                      <SkipForward className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
